import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toDay } from "@/lib/time";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const field = "rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11";

export default function EventDialog({ event, weekStart, onClose, onSaved, viewUserId }) {
  const [form, setForm] = useState({ title: "", date: "", start: "10:00", end: "11:00" });
  const editing = Boolean(event?.id);

  useEffect(() => {
    if (!event) return;
    if (event.id) {
      setForm({ title: event.title, date: event.start.slice(0, 10), start: event.start.slice(11, 16), end: event.end.slice(11, 16) });
    } else {
      const today = new Date();
      setForm({ title: "", date: toDay(today >= weekStart ? today : weekStart), start: "10:00", end: "11:00" });
    }
  }, [event, weekStart]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const body = { title: form.title, start: `${form.date}T${form.start}`, end: `${form.date}T${form.end}`, user_id: viewUserId || null };
    try {
      if (editing) await api.put(`/events/${event.id}`, body);
      else await api.post("/events", body);
      toast.success("Time blocked. Tasks re-planned around it.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/events/${event.id}`);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={Boolean(event)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-md" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">{editing ? "Edit blocked time" : "Block time"}</DialogTitle>
          <DialogDescription>Meetings, client calls or time off. Tasks won't be scheduled here.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input required value={form.title} onChange={set("title")} className={field} placeholder="e.g. Client meeting" data-testid="event-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" required value={form.date} onChange={set("date")} className={field} data-testid="event-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input type="time" required value={form.start} onChange={set("start")} className={field} data-testid="event-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input type="time" required value={form.end} onChange={set("end")} className={field} data-testid="event-end-input" />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2">
            {editing ? (
              <button type="button" onClick={remove} data-testid="event-delete-button" className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors duration-200">
                <Trash2 size={15} /> Delete
              </button>
            ) : <span />}
            <button type="submit" data-testid="event-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">
              Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
